import React, { useEffect, useState } from "react";
import CustomTable from "../../components/customtable/CustomTable";
import DebounceSearch from "../../components/common/DebounceSearch";
import Breadcrumb from "../../components/common/Breadcrumb";
import Action from "../../components/common/Action";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import {
  deleteUsers,
  getAllUserslist,
  getUserslist,
} from "../../redux/user/slice";
import RippleLoader from "../../components/RippleLoader/RippleLoader";
import { SortDownArrow } from "../../assets/svg";
import { toast } from "react-toastify";

const User = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [userDelete, setUserDelete] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);

  // selector
  const { userList, userListLoading, allUserList, allUserListLoading } =
    useSelector((store) => ({
      userList: store?.usersData?.getUserslistStatus?.data?.data,
      allUserList: store?.usersData?.getAllUserslistStatus?.data?.data,
      userListLoading: store?.usersData?.loading,
      allUserListLoading: store?.usersData?.alluserloading,
    }));

  //call api to get full user list for export
  useEffect(() => {
    dispatch(getAllUserslist());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getUserslist({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
      })
    );
    dispatch(getAllUserslist());
  }, [dispatch, searchValue, pageCount, pageLimit, sortBy, sortDirection]);

  // Delete user
  const deleteUserId = () => {
    setIsLoading(true);
    if (!userDelete) return;
    dispatch(deleteUsers(userDelete)).then((res) => {
      setIsLoading(false);
      if (res.type === "deleteUsers/fulfilled") {
        dispatch(
          getUserslist({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
          })
        );
      }
      setConfirmDelete(false);
    });
  };

  // Export CSV

  const handleExportCSV = () => {
    if (!allUserList?.length) {
      toast("No users available to export.");
      return;
    }

    const csvHeaders = ["First Name", "Last Name", "Email", "Auth Type"];

    const csvRows = allUserList.map((user) => [
      user.firstName || "",
      user.lastName || "",
      user.email || "",
      user.authType || "",
    ]);

    // Convert to CSV format
    const csvContent = [
      csvHeaders.join(","), // header row
      ...csvRows.map((row) => row.join(",")), // data rows
    ].join("\n");

    // Download CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "users.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Table Headers
  const ColumnHeaders = () => (
    <>
      <th className="w-24 table-th">No.</th>
      <th className="table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("firstName");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>First Name</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "firstName"
                ? "rotate-180"
                : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("lastName");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Last Name</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "lastName"
                ? "rotate-180"
                : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("email");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Email</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "email"
                ? "rotate-180"
                : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="w-32 table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("authType");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Auth Type</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "authType"
                ? "rotate-180"
                : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="w-24 table-th">Actions</th>
    </>
  );

  // Table Rows
  const DataRows = () => (
    <>
      {(userList?.data || []).map((item, index) => (
        <tr key={item.id} className=" shadow-sm text-mgrey hover:bg-navTheme/5">
          <td className="px-5 py-3">
            {(pageCount - 1) * pageLimit + index + 1}
          </td>
          <td className="px-5 py-3">
            <div className="line-clamp-1">{item?.firstName || "-"}</div>
          </td>
          <td className="px-5 py-3">
            <div className="line-clamp-1">{item?.lastName || "-"}</div>
          </td>
          <td className="px-5 py-3 truncate" title={item?.email || "-"}>
            {item?.email || "-"}
          </td>
          <td className="px-5 py-3">{item?.authType || "-"}</td>
          <td className="flex px-5 py-3">
            <Action
              optionList={
                <>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => navigate(`/view-user/${item?._id}`)}
                  >
                    View
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() =>
                      navigate("/edit-user", {
                        state: { user: item, mode: "edit" },
                      })
                    }
                  >
                    Edit
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => {
                      setConfirmDelete(true);
                      setUserDelete(item?._id);
                    }}
                  >
                    Delete
                  </li>
                </>
              }
            />
          </td>
        </tr>
      ))}
    </>
  );
  return (
    <>
      <div className="space-y-5">
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">User</h6>
        </div>
        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbActive={"User"}
          breadCrumbTitleKey={"/"}
          classes=""
        />
        <div className="flex items-center gap-4 w-full">
          <DebounceSearch
            className="flex-1"
            search={searchValue}
            handleChange={setSearchValue}
            placeholder="Search..."
          />

          <button
            className="bg-themeBtn text-white px-5 py-3 rounded-md font-medium hover:bg-themeBtn/90 transition"
            type="button"
            onClick={handleExportCSV}
          >
            {allUserListLoading ? "Loading..." : "Export Users"}
          </button>
        </div>

        <div className="w-full bg-white shadow rounded-lg">
          {userListLoading ? (
            <RippleLoader />
          ) : (
            <CustomTable
              overflowVisibleLg={true}
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={userList}
              loading={userListLoading}
              disableNext={!!userList?.data?.length}
              disablePrev={!!userList?.data?.length}
              showPagination={true}
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              current={pageCount}
              total={userList?.pagination?.total}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(userList?.pagination?.total / pageLimit)}
            />
          )}
        </div>
      </div>
      {confirmDelete && (
        <div>
          <ConfirmationModel
            confirmText={"Delete"}
            onClose={() => {
              setConfirmDelete(false);
            }}
            onConfirm={deleteUserId}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default User;
